import { useConfigsStore } from '@/stores/configs'
import { result as _result, isObject } from 'lodash-es'
import { commons } from '@provise/lib/transformer'
import { replaceTitle } from '@/lib/dropdowns'

////---

/**
 * @callback ChangeFormatter
 * @param {Object} change certain history entry
 * @param {Object} relations all given relations as {RelName:{RelId: Rel}}
 */

/**
 * @callback ChangeValueGetter
 * @param {Object} changeset full changeset (batch)
 * @param {Object} change certain history entry
 * @param {Object|String} relation relation name
 * @param {String} prop what prop in change entry value|old_value
 */

////---

export const models = {
  Activities: {
    entityName: ch => `Активность ${ch.model_id}`,
    prefix: 'activities',
  },
  ActivityTypes: {
    entityName: ch => `Тип активности ${ch.model_id}`,
    prefix: 'catalog/activity_types',
  },
  Consumables: {
    entityName: (_, rel) => `Расходный материал «${rel}»`,
    prefix: 'catalog/consumables',
  },
  Comments: {
    entityName: () => '',
  },
  ConsumablesExpenses: {
    entityName: (_, rel) => `Расходные материалы «${rel}»`,
  },
  ConsumablesPurchases: {
    entityName: ch => `Закупка расходных материалов №${ch.model_id}`,
  },
  ConsumablesStock: {
    entityName: (_, rel) => `Склад расх. материалы «${rel}»`,
    prefix: 'stock/consumables',
  },
  Devices: {
    entityName: (_, rel) => `Колонка/устройство «${rel}»`,
    prefix: 'catalog/devices',
  },
  DevicesExpenses: {
    entityName: (_, rel) => `Колонки и устройства «${rel}»`,
  },
  DevicesPurchases: {
    entityName: ch => `Закупка колонок и устройств №${ch.model_id}`,
  },
  DevicesStock: {
    entityName: (_, rel) => `Склад устройств «${rel}»`,
    prefix: 'stock/devices',
  },
  Laboratories: {
    entityName: (_, rel) => `Лаборатория «${rel}»`,
    prefix: 'catalog/laboratories',
  },
  MaterialsComm: {
    entityName: (_, rel) => `Материалы коммерческие «${rel}»`,
    prefix: 'catalog/materials_comm',
  },
  MaterialsCommExpenses: {
    entityName: (_, rel) => `Материалы коммерческие «${rel}»`,
  },
  MaterialsCommPurchases: {
    entityName: ch => `Закупка коммерческих материалов №${ch.model_id}`,
  },
  MaterialsCommStock: {
    entityName: (_, rel) => `Склад материалы ком. «${rel}»`,
    prefix: 'stock/materials_comm',
  },
  MaterialsNd: {
    entityName: (_, rel) => `Материалы фирмы «${rel}»`,
    prefix: 'catalog/materials_nd',
  },
  MaterialsNdExpenses: {
    entityName: (_, rel) => `Материалы фирмы «${rel}»`,
  },
  MaterialsNdPurchases: {
    entityName: ch => `Закупка материалов фирмы №${ch.model_id}`,
  },
  MaterialsNdStock: {
    entityName: (_, rel) => `Склад материалы НД «${rel}»`,
    prefix: 'stock/materials_nd',
  },
  Medicines: {
    entityName: (ch, rel, il) =>
      `Запись в справочнике Лекарственный препарат «${genLink(ch, rel, il)}»`,
    prefix: 'catalog/medicines',
  },
  MedicinesExpenses: {
    entityName: (_, rel) => `Препараты «${rel}»`,
  },
  MedicinesPurchases: {
    entityName: ch => `Закупка препаратов №${ch.model_id}`,
  },
  MedicinesStock: {
    entityName: (_, rel) => `Склад лп «${rel}»`,
    prefix: 'stock/medicines',
  },
  MedicineTrademarks: {
    entityName: (ch, rel, il) => `Запись в справочнике Торговая марка «${genLink(ch, rel, il)}»`,
    prefix: 'catalog/medicine_trademarks',
  },
  Orders: {
    entityName: ch => `Заявка ${ch.model_id}`,
    prefix: 'orders',
  },
  Templates: {
    entityName: (_, rel) => `Шаблон «${rel}»`,
    prefix: 'templates',
  },
  Vendors: {
    entityName: (_, rel) => `Поставщик «${rel}»`,
    prefix: 'catalog/vendors',
  },
  RequestCategory: {
    entityName: (ch, rel, il) => `Категория обращения «${genLink(ch, rel, il)}»`,
    prefix: 'request_category',
  },
  CustomerCategory: {
    entityName: (ch, rel, il) => `Категория клиента «${genLink(ch, rel, il)}»`,
    prefix: 'customer_category',
  },
  DocTemplates: {
    entityName: (ch, rel, il) => `Бланк документа «${genLink(ch, rel, il)}»`,
    prefix: 'doc_templates',
  },
  EmailTemplates: {
    entityName: (ch, rel, il) => `Шаблон ответа «${genLink(ch, rel, il)}»`,
    prefix: 'email_templates',
  },
  Departments: {
    entityName: (ch, rel, il) => {
      const departments = rel?.Departments
      if (Array.isArray(departments)) {
        const depart = departments.find(d => d.id === ch.model_id)
        return `${depart?.type?.value || 'Подразделение'} "<a href='/${depart?.type?.key || 'department'}/${depart.id}'>${depart.name}</a>"`
      } else {
        return `Подразделение ID=${ch.model_id}`
      }
    },
    prefix: 'departments',
  },
  Complaints: {
    entityName: (ch, rel, il) => {
      const complaints = rel?.Complaints
      if (Array.isArray(complaints)) {
        const complaint = rel?.Complaints.find(d => d.id === ch.model_id)
        return `Обращение  ID=${genLink(ch, complaint.qems_id, il)}`
      } else {
        const id = complaints[ch.model_id]
        return `Обращение  ID=${genLink(ch, id, il)}`
      }
    },
    prefix: 'complaints',
    reflink: (_, id) => `<a href="/complaint/${id}">${id}</a>`,
  },
  Communication: {
    entityName: (ch, rel) => {
      const comm = rel?.Communication.find(d => d.id === ch.model_id)
      if (comm?.channel && comm?.channel?.key === 'email') {
        const mails = comm?.email_addresses?.map(e => e.email).join(', ')
        return `Коммуникация на канал коммуникации "по email" (${mails})`
      }
      return `Коммуникация на канал коммуникации "${comm?.channel?.value || 'по телефону'}"`
    },
  },
  Uploads: {
    entityName: ch => `загрузка файла ${ch?.changes?.[0]?.filename}`,
  },
  SLA: {
    entityName: (_, rel) => `этап: ${stages[rel?.status] || ''}`,
  },
  OfficialReplies: {
    entityName: (_, rel) => {
      if (rel.name) {
        return `Ответ "${rel.name}" бланк "${rel.doc_template?.name || 'нет'}"`
      }
      if (rel.uploads?.[0]?.filename) {
        return `Ответ "${rel.uploads[0].filename}"`
      }

      if (typeof rel === 'string') {
        const parts = rel.split('|')
        if (parts.length === 2) {
          return `Ответ ${parts[0]} на обращение ID=<a href="/complaint/${parts[1]}">${parts[1]}</a>`
        }
      }

      return `Ответ ${rel}`
    },
  },
  Transfers: {
    entityName: ch => `Трансфер образца ID=${ch.model_id}`,
  },
  EmailAddress: {
    entityName: (ch, rel) => {
      const foundCommunication = rel?.Communication.find(d =>
        d.email_addresses.some(d1 => d1.id === ch.model_id)
      )
      const foundEmail = foundCommunication?.email_addresses.find(
        d1 => d1.id === ch.model_id
      )?.email
      return `email адрес ${foundEmail || ''}`
    },
  },
  User: {
    entityName: (ch, rel, il) => {
      if (isObject(rel)) {
        rel = rel?.fio || rel?.username
      }
      return `Пользователь ${genLink(ch, rel, il)}`
    },
    prefix: 'users',
  },
  Dropdowns: {
    entityName: (ch, rel) => {
      const field = ch?.changes?.[0]?.field || ''
      const translated = field
        .split('.')
        .map(part => replaceTitle(part))
        .join(' / ')
      return `Выпадающий список ${translated} ${rel}`
    },
  },
  DropdownsCatalog: {
    entityName: (ch, rel) => {
      const field = ch?.changes?.[0]?.field || ''
      const translated = field
        .split('.')
        .map(part => replaceTitle(part))
        .join(' / ')
      return `Запись в справочнике Выпадающий список ${translated} ${rel}`
    },
  },
}

const stages = {
  registration: 'Регистрация',
  correction: 'Уточнение данных',
  investigation: 'Расследование',
  prepare_to_answer: 'Подготовка ответа',
  coordination: 'Согласование ответа',
  delivery_answer: 'Отправка ответа',
}

function genLink(ch, txt, il) {
  const model = models[ch.model_name]
  return il
    ? model
      ? typeof model.reflink === 'function'
        ? model.reflink(ch, txt)
        : `<a href="/${model.prefix}/${ch.model_id}">${txt}</a>`
      : txt
    : txt
}

const fieldLabels = {
  activity_subtype: 'Тип активности',
  activity_type: 'Тип активности',
  address: 'Адрес',
  airbill: '№ авианакладной',
  aligned_by: 'согласующий сотрудник',
  aligned: 'согласование',
  commerce_aligned_by: 'согласующий сотрудник коммерческого отдела',
  commerce_aligned: 'согласование коммерческого отдела',
  amount: 'количество',
  assign_role: 'назначена группа',
  assign_user: 'назначен исполнитель',
  bill_date: 'Дата счета',
  bill: '№ счета',
  comment: 'комментарий',
  consignment_date: 'Дата УПД/ТН',
  consignment: '№ УПД/ТН',
  consumable: 'Расходный материал',
  contact: 'Контактное лицо',
  device: 'Колонка или устройство',
  dispatch_date_expected: 'Дата поставки плановая',
  dosage: 'дозировка',
  gtd_date: 'Дата ГТД',
  gtd: '№ ГТД',
  invoice: '№ инвойса',
  laboratory: 'Лаборатория',
  manufacturer: 'Производитель',
  material_comm: 'Материал',
  material_nd: 'Материал',
  medicine_trademark: 'Принадлежность к препарату',
  mhh: 'МНН',
  name_eng: 'Наименование на английском',
  name_short: 'Сокращение',
  name: 'Название',
  order_date: 'Дата заказа',
  os_auc: '№ ОС AUC',
  os: '№ ОС',
  packing: 'Фасовка',
  pay_date_executed: 'Дата оплаты фактическая',
  pay_date_expected: 'Планируемая дата оплаты',
  phone: 'Телефон',
  po_approval_date: 'Дата одобрения РО',
  po_delivery_date: 'Delivery date РО',
  po_status: 'Статус PO',
  po_summ: 'Сумма PO',
  po: 'PO',
  price: 'Цена',
  received_amount: 'Количество в поставке',
  receiving_date_actual: 'Дата поставки фактическая',
  receiving_date_planned: 'Дата поставки плановая',
  registration_at: 'Дата регистрации',
  serie: 'Номер серии',
  shared_order: 'Резерв',
  shipment_id: 'ID отгрузки',
  spec_date: 'Дата Спецификации/КП',
  spec_serial: 'Порядковый номер в спецификации',
  spec: '№ спецификации',
  status: 'статус',
  stock: 'склад',
  storage_conditions: 'Условия хранения',
  total_price: 'Общая стоимость',
  unit_price: 'Стоимость за единицу',
  unit: 'Ед. измерения',
  valid_till: 'Срок годности',
  vendor: 'Поставщик',
  vortex_go_id: 'ID CC Vortex',
  vortex_reg_id: 'ID REG Vortex',
  vortex_loc_id: 'ID LOC Vortex',
  roles: 'Роли',
  transfer_pickup_address: 'Адрес забора образца',
  _subcomplaints: 'Связанные события ID ',
  //
  Activities: {
    shipment_date_scheduled: 'Планируемая дата подачи',
  },
  ActivityTypes: {
    name: 'Тип активности',
    parent_type: 'Наименование активности',
  },
  Consumables: {
    code: 'Каталожный номер',
  },
  Devices: {
    code: 'Каталожный номер',
    diameter: 'Диаметр',
    length: 'Длина',
    particle_size: 'Размер частиц',
    pore_size: 'Размер пор',
    sorbent: 'Сорбент',
  },
  MaterialsNd: {
    code: 'Номер лота',
  },
  Medicines: {
    product_form: 'Форма выпуска',
    registration_certificate: '№ Регистрационного удостоверения',
  },
  MedicineTrademarks: {
    code: 'Код продукта',
    medicinal_form: 'Лекaрственная форма',
  },
  Orders: {
    linked_order: 'Связанная заявка',
    testing_price: 'Стоимость тестирования без НДС',
    testing_tax_price: 'Стоимость тестирования c НДС',
    dispatch_date_executed: 'Фактическая дата отправки',
    test_date_expected: 'Планируемая дата тестирования',
    test_date_executed: 'Дата тестирования фактическая',
    date_closed: 'Дата завершения и разнесения',
  },
  User: {
    email_notifications: 'E-mail',
    notifications_level: 'Уровень получения сообщений',
    fio: 'ФИО',
    username: 'Логин',
    email: 'PRID',
  },
  Complaints: {
    request_category: 'Категория обращения',
    name: 'ФИО инициатора обращения',
    ur_name: 'Наименование организации',
    contact: 'Должность',
    phone: 'Телефон',
    email: 'E-mail',
    market_appeal: 'Обращение по фронтьерному рынку',
    registration_country: 'Страна',
    medicine: 'Лекарственный препарат, дозировка, форма выпуска',
    series: 'Серия',
    channel: 'Канал получения претензии',
    agreement: 'Согласие на обработку ПД',
    description: 'Исходное описание претензии/обращения',
    name_type: 'Тип инициатора обращения',
    city: 'Город',
    department: 'Подразделение, на которое будет отправлена информация об обращении',
    close: 'Закрыть обращение после передачи',
    product_expiration: 'Срок годности',
    product_production_date: 'Дата производства',
    product_manufacturer_name: 'Название производителя, указанное на пачке',
    product_units: 'Единицы измерения',
    product_defect: 'Количество упаковок с дефектом',
    investigation_result: 'Результат расследования',
    need_commercial_approve: 'Уведомить коммерческий отдел',
    no_approve: 'Перейти к ответу без согласования',
    commerce_aligned: 'Подписано руководителем коммерческого отдела',
    approval_comment: 'Комментарий по согласованию',
    name_legal_entity: 'Наименование юр. лица',
    description_used: 'Как использовался препарат',
    pharm_invoice: 'Товарный/кассовый чек в наличии',
    pharm_contact: 'Контактные данные аптеки',
    pharm_address: 'Адрес аптеки',
    pharm_name: 'Название аптеки',
    serializ_code: 'Сериализационный код продукта',
    fn_number: 'Номер Фармаконадзора',
    med_number: 'Номер МедИнфо',
    date_response_med: 'Дата получения ответа (МедИнфо)',
    date_response_pharm: 'Дата получения ответа (ФН)',
    pharm_method: 'Способ получения лекарственного препарата',
    pharm_date_purchase:
      'Дата приобретения лекарственного препарата, вызвавшего сомнения в подлинности',
    pharm_country: 'Страна',
    fn_criticality_event: 'Серьезность',
    product_code: 'Код продукта',
    date_notification_manufacturer: 'Дата информирования Производителя об отклонении по качеству',
    product_package: 'Название лекарственного препарата на вторичной упаковке',
    place_event: 'Место события',
    product_suspect: 'Подозреваемый лекарственный препарат',
    place_country: 'Страна',
    person_eported_claim: 'Контактное лицо/организация, сообщившее о претензии',
    addit_location: 'Место выявления хищения',
    date_notification_police: 'Дата информирования полиции',
    date_notification_rzn: 'Дата информирования РЗН',
    product_price: 'Стоимость',
    registration_post: 'Должность',
    date_registration_sp: 'Дата регистрации в SP',
    number_sp: 'Номер записи в SP',
    applicable_swedish: 'Шведская поставка',
    date_confirm: 'Дата получения подтверждения',
    confirm_manufacturer: 'Получено подтверждение от Производителя',
    date_finish: 'Время завершения этапа',
    date_eqv_finish: 'Дата завершения расследования',
    date_closed: 'Дата завершения',
    product_number_invoice: '№ поставки, инвойса',
  },
  CustomerCategory: {
    parent: 'Родительская',
  },
  RequestCategory: {
    close_on_stage: 'Закрыть на этапе',
    parent: 'Родительская',
  },
  DocTemplates: {
    text: 'Текст',
  },
  EmailTemplates: {
    text: 'Текст',
  },
  Departments: {
    person: 'Контактное лицо',
    position: 'Должность контактного лица',
    contacts: 'Контакты',
  },
  Transfers: {
    address: 'Точный адрес получателя',
    amount: 'Сумма',
    assistant: 'Ассистент',
    city: 'Город',
    comment_1: 'Комментарий',
    company: 'Название компании',
    completeness: 'Комплектность образца',
    content: 'Текст сообщения',
    cost: 'Cost Centre',
    courier: 'Курьерская служба',
    date_created: 'Дата создания запроса',
    date_receive: 'Дата получения образца',
    date_sending: 'Дата отправки образца',
    department: 'Отдел',
    invoice: 'Номер накладной',
    phone: 'Телефон получателя',
    quality_group: 'Образец получен группой по качеству',
    receiver: 'Получатель',
    receiver_lastname: 'Фамилия получателя',
    sender: 'Отправляющий',
    status: 'Статус',
    template: 'Шаблон',
    weight: 'Вес, кг',
    comment_2: 'Содержание отправки',
    transferred: 'Образец передан',
    date_request_sent: 'Дата отправки запроса',
  },
  Communication: {
    channel: 'Канал коммуникации',
    email_text: 'Сопроводительный текст в электронной почте',
    repeat_mess: 'Повторное обращение',
    doc_template: 'Бланк документа',
    doc_text: 'Текст, который будет напечатан на бланке',
    address: 'Получатель',
  },
  SLA: {
    date_finish: 'Срок выполнения',
  },
  OfficialReplies: {
    doc_template: 'Шаблон документа',
    doc_text: 'Текст документа',
    address: 'Получатель',
  },
}

// if function, here then no composition
// the function must return a string as hist entry
export const fields = {
  ...fieldLabels,
  assign_user: fmtAssignedUser,
  approval_comment: fmtSkipField,
  request_change: fmtRequestChange,
  sent: fmtSent,
  Comments(ch) {
    const comment = JSON.parse(ch.value)
    return `<span class="text-muted">добавлен комментарий:</span> ${comment.text}`
  },
}

export const values = () => ({
  activity_subtype: valueRelation,
  activity_type: valueRelation,
  aligned: valueAligned('aligned_by'),
  commerce_aligned: valueAligned('commerce_aligned_by'),
  aligned_by: valueAssignUser,
  commerce_aligned_by: valueAssignUser,
  assign_role: valueRelation,
  assign_user: valueAssignUser,
  bill_date: valueDate,
  consignment_date: valueDate,
  consumable: valueRelation,
  date_created: valueDate,
  device: valueRelation,
  dispatch_date_expected: valueDate,
  gtd_date: valueDate,
  laboratory: valueRelation,
  material_comm: valueRelation,
  material_nd: valueRelation,
  medicine_trademark: valueRelation,
  order_date: valueDate,
  parent_type: valueRelation,
  pay_date_executed: valueDate,
  pay_date_expected: valueDate,
  po_approval_date: valueDate,
  po_delivery_date: valueDate,
  receiving_date_actual: valueDate,
  receiving_date_planned: valueDate,
  registration_at: valueDate,
  spec_date: valueDate,
  roles: valueRelation,
  //complaints dates
  received_date: valueDate,
  date_problem: valueDate,
  pharm_date_purchase: valueDate,
  product_expiration: valueDate,
  product_production_date: valueDate,
  date_notification_rzn: valueDate,
  date_notification_police: valueDate,
  coa_certificate_date: valueDate,
  coa_date_sent: valueDate,
  coa_date_translated: valueDate,
  date_response_pharm: valueDate,
  date_response_med: valueDate,
  date_notification_deviation: valueDate,
  date_registration_sp: valueDate,
  date_notification_manufacturer: valueDate,
  date_confirm: valueDate,
  date_department: valueDate,
  date_response: valueDate,
  date_eqv_finish: valueDate,
  date_finish: valueDate,
  date_real_finish: valueDate,
  date_closed: valueDate,
  //complaints bools
  med_received: valueBool,
  agreement: valueBool,
  close: valueBool,
  spam_close: valueBool,
  department_message_sent: valueBool,
  fn_received: valueBool,
  used_as_lp: valueBool,
  pharm_invoice: valueBool,
  product_other: valueBool,
  sample_is_available: valueBool,
  sample_on_stock: valueBool,
  description_photo: valueBool,
  provide_results: valueBool,
  applicable_swedish: valueBool,
  confirm_manufacturer: valueBool,
  market_appeal: valueBool,
  need_commercial_approve: valueBool,
  no_approve: valueBool,

  /**
   * @type {ChangeValueGetter} stock
   */
  stock: function (_, change, __, prop) {
    const v = valueRelation(...arguments)
    return v ? `Серия ${v}` : change[prop]
  },
  valid_till: valueDate,
  vendor: valueRelation,
  //
  Activities: {
    shipment_date_scheduled: valueDate,
  },
  Consumables: {
    packing: valueConfigId('consumables.packing'),
    storage_conditions: valueConfigId('consumables.storage_conditions'),
  },
  ConsumablesExpenses: {
    status: valueConfigId('consumables_expenses.status'),
  },
  ConsumablesPurchases: {
    status: valueConfigId('consumables_purchases.status'),
  },
  ConsumablesStock: {
    status: valueConfigId('consumables_stock.status'),
  },
  //
  Devices: {
    packing: valueConfigId('devices.packing'),
    storage_conditions: valueConfigId('devices.storage_conditions'),
  },
  DevicesExpenses: {
    status: valueConfigId('devices_expenses.status'),
  },
  DevicesPurchases: {
    status: valueConfigId('devices_purchases.status'),
  },
  DevicesStock: {
    status: valueConfigId('devices_stock.status'),
  },
  //
  MaterialsComm: {
    packing: valueConfigId('materials_comm.packing'),
    storage_conditions: valueConfigId('materials_comm.storage_conditions'),
  },
  MaterialsCommExpenses: {
    status: valueConfigId('materials_comm_expenses.status'),
  },
  MaterialsCommPurchases: {
    status: valueConfigId('materials_comm_purchases.status'),
  },
  MaterialsCommStock: {
    status: valueConfigId('materials_comm_stock.status'),
  },
  //
  MaterialsNd: {
    packing: valueConfigId('materials_nd.packing'),
    storage_conditions: valueConfigId('materials_nd.storage_conditions'),
  },
  MaterialsNdExpenses: {
    status: valueConfigId('materials_nd_expenses.status'),
  },
  MaterialsNdPurchases: {
    status: valueConfigId('materials_nd_purchases.status'),
  },
  MaterialsNdStock: {
    status: valueConfigId('materials_nd_stock.status'),
  },
  //
  Medicines: {
    packing: valueConfigId('medicines.packing'),
    product_form: valueConfigId('medicines.product_form'),
    storage_conditions: valueConfigId('medicines.storage_conditions'),
  },
  MedicinesExpenses: {
    status: valueConfigId('medicines_expenses.status'),
  },
  MedicinesPurchases: {
    status: valueConfigId('medicines_purchases.status'),
  },
  MedicinesStock: {
    status: valueConfigId('medicines_stock.status'),
  },
  MedicineTrademarks: {
    medicinal_form: valueConfigId('medicine_trademarks.medicinal_form'),
  },
  //
  Orders: {
    status: valueConfigId('orders.status'),
    dispatch_date_executed: valueDate,
    test_date_expected: valueDate,
    test_date_executed: valueDate,
    date_closed: valueDate,
  },
  RequestCategory: {
    parent: valueRelation,
  },
  CustomerCategory: {
    parent: valueRelation,
  },
  Transfers: {
    date_created: valueDate,
    date_receive: valueDate,
    date_sending: valueDate,
    date_request_sent: valueDate,
  },
  User: {
    notifications_level: function (_, change, __, prop) {
      const notifications_level = [
        { id: 0, value: 'Не отправлять' },
        { id: 1, value: 'Только критические' },
        { id: 2, value: 'Критические и обычные' },
        { id: 3, value: 'Все' },
      ]
      let n = notifications_level.find(d => d.id === Number(change[prop]))
      return n?.value || ''
    },
  },
  Complaints: {
    product_stolen: function (_, change, __, prop) {
      const product_stolen_val = [
        { id: 'u', value: 'Неизвестно' },
        { id: 'y', value: 'Да' },
        { id: 'n', value: 'Нет' },
      ]
      let n = product_stolen_val.find(d => d.id === change[prop])
      return n?.value || ''
    },
  },
})

export function url(model, id) {
  if (!models[model]?.prefix) {
    return null
  }
  return `${models[model].prefix}/${id}/history`
}
// --
/**
 * @type {ChangeValueGetter} valueAssignUser
 */
function valueAssignUser(_, change, rel, prop) {
  let u = _result(rel, [change.relation, change[prop]])
  if (Array.isArray(rel['User'])) {
    u = rel['User'].find(d => d.id === Number(change[prop]))
  }
  return u?.fio || u?.username
}
/**
 * @type {ChangeValueGetter} valueAligned
 */
function valueAligned(fieldName) {
  return function (batch, change, __, prop) {
    const firstTimeAligned =
      (batch.changes.find(c => c.path.includes(fieldName)) ?? {}).old_value === null &&
      prop === 'old_value'

    let val = change[prop]
    switch (change[prop]) {
      case 'True':
      case true:
        val = 'согласовано'
        break
      case 'False':
      case false:
        val = firstTimeAligned ? null : 'отклонено'
        break
    }
    return val
  }
}
/**
 * @type {ChangeValueGetter} valueRelation
 */
function valueRelation(_, change, rel, prop) {
  return _result(rel, [change.relation, change[prop]], change[prop])
}
/**
 * @param {string} pth
 * @returns {ChangeValueGetter}
 */
function valueConfigId(pth) {
  const configStore = useConfigsStore()
  return (_, change, __, prop) => {
    const v = configStore.getById(pth, change[prop])
    return v
  }
}
/**
 * @type {ChangeValueGetter} valueDate
 */
function valueDate(_, change, __, prop) {
  const val = change[prop]
  if (!val || val === '0000-00-00' || isNaN(new Date(val).getTime())) {
    return null
  }
  return commons.dateLocal(val)
}
/**
 * @type {ChangeValueGetter} valueBool
 */
function valueBool(_, change, __, prop) {
  const val = change[prop];

  if (val === 'True') {
    return 'Да';
  }

  if (val === 'False') {
    return 'Нет';
  }

  return 'Не известно';
}
// --
/**
 * @type {ChangeFormatter} fmtAssignedUser
 */
function fmtAssignedUser(change, relations) {
  if (change.value === null) {
    return `снят ответственный`
  }
  let rel = _result(relations, [change.relation, change.value], {})
  if (Array.isArray(relations[change.relation])) {
    rel = relations[change.relation].find(d => d.id === Number(change.value))
  }
  const str = [
    `назначен ответственный <span class="text-success">${rel?.fio || rel?.username}</span>`,
  ]
  return str.join(' ')
}
/**
 * @type {ChangeFormatter} fmtSkipField
 */
function fmtSkipField() {
  return ''
}
/**
 * @type {ChangeFormatter} fmtSent
 */
function fmtSent(change) {
  if (change.value === 'True') return `отправлено`
  return ''
}
/**
 * @type {ChangeFormatter} fmtRequestChange
 */
function fmtRequestChange(change) {
  return `запрошена смена ответственного с ${change.old_value} на ${change.value}`
}
